// Poll a deployment's logs while it's in flight.
//
// On mount (or when re-enabled) it fetches the full log once, and while the
// record is non-terminal (still deploying or its post-deploy watch is running)
// it re-fetches on an interval, replacing the whole payload each time (v1 has
// no incremental cursor use — a full refetch keeps the reassembly trivial).
// When the record becomes terminal it does exactly one final fetch and stops.
//
// Correlation and overlap handling live in the data layer's fetchLogs; here we
// simply avoid issuing a new poll while one is still resolving, and ignore any
// resolution that arrives after this consumer has moved on.

import { useCallback, useEffect, useRef, useState } from 'react';
import { DeploymentLogs, DeploymentRecord } from '../../types/DeploymentRecord';
import { useDeploymentData } from './useDeploymentData';
import { isActiveDeploy } from '../lib/grouping';

/** How often to re-fetch while a deployment is still in flight. */
export const LOGS_POLL_INTERVAL_MS = 4_000;

export interface UseDeploymentLogsResult {
  logs: DeploymentLogs | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

/**
 * Live-tailing logs for one deployment record.
 *
 * @param record   the deployment whose logs to fetch; its status drives polling
 * @param enabled  gate polling (e.g. only while the Logs tab / dialog is open)
 */
export const useDeploymentLogs = (
  record: DeploymentRecord,
  enabled = true
): UseDeploymentLogsResult => {
  const { fetchLogs } = useDeploymentData();
  const [logs, setLogs] = useState<DeploymentLogs | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);

  const deploymentId = record.deployment_id || '';
  // Non-terminal → keep polling. Terminal → one final fetch, then stop.
  const active = isActiveDeploy(record);

  // Latest resolved logs, read inside the effect without making it a dependency
  // (so we can suppress the big "loading" state on background polls).
  const logsRef = useRef<DeploymentLogs | null>(null);
  logsRef.current = logs;

  const refresh = useCallback(() => setRefreshTick(t => t + 1), []);

  useEffect(() => {
    if (!enabled) return;
    if (!deploymentId) {
      setError('No deployment id for this record');
      setLoading(false);
      return;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const tick = async () => {
      // Only surface the full-screen loading state on the very first load;
      // background polls refresh silently so the panel doesn't flicker.
      if (logsRef.current === null) setLoading(true);
      try {
        const result = await fetchLogs(deploymentId);
        if (cancelled) return;
        setLogs(result);
        setError(null);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
      // Schedule the next poll only while still in flight. When `active` flips
      // to false this effect re-runs (see deps) and does one final fetch here
      // without scheduling another — the "one last fetch then stop" behavior.
      if (!cancelled && active) {
        timer = setTimeout(tick, LOGS_POLL_INTERVAL_MS);
      }
    };

    void tick();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [enabled, deploymentId, active, fetchLogs, refreshTick]);

  return { logs, loading, error, refresh };
};
